import React from 'react';
import { useSelector } from 'react-redux';
import { NewHireProps } from '../../../../models/arc-epi-props';
import { CoverageFormModel, CoveragePlans, DeclinedValue, NewHireCoverageState, PlanTypes, TiersTypes } from '../models/coverage-models';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import { CalculatedTiers } from '../../models/new-hire-enrollment-models';
import { getTierLabel } from '../helpers/coverage-display-helpers';
import { getCurrencyFormat } from '../../../../../../global/helpers/format-helpers';
import { LabelSettingProps } from '../../../../../../global/models/epi-model';
import { WhoWillEnrollCriterias, WhoWillEnrollOptions } from '../../models/who-will-enroll-models';
import { ParticipantEnrollmentSettings } from '../../../new-participant-enrollment/models/epi-props';
import { validatePlanContentDioApprover } from '../helpers/coverage-form-helpers';



interface Props {
	settings: NewHireProps;
	formValues: CoverageFormModel;
	coverageState: NewHireCoverageState;
	labelSettings: LabelSettingProps;
	isParticipant?: boolean;
	isDioApprover?: boolean;
}

export function HealthPlanReview(props: Props) {

	const { settings, formValues, coverageState, labelSettings, isParticipant, isDioApprover } = props;
	const displayTierMedical = useSelector<TcsmStates, CalculatedTiers>(state => state?.newHireEnrollmentState?.tierToDisplayMedical);
	const displayTierDental = useSelector<TcsmStates, CalculatedTiers>(state => state?.newHireEnrollmentState?.tierToDisplayDental);
	const whoWillEnroll = useSelector<TcsmStates, WhoWillEnrollCriterias>(state => state?.newHireEnrollmentState.whoWillEnrollCriteria);
	const participantSettings = useSelector<TcsmStates, ParticipantEnrollmentSettings>(state => state.epiProps?.participantEnrollmentSettingsState?.epiContent);
	const hasMedical = coverageState?.eligible_plans?.medical?.plans?.length > 0;
	const hasDental = coverageState?.eligible_plans?.dental?.plans?.length > 0;

	function RenderPlanReview(
		planType: PlanTypes,
		declineText: string,
		plans: CoveragePlans,
		tier: CalculatedTiers,
		whoWillEnrollOption: WhoWillEnrollOptions,
		pushToMemberCheckMessage: string,
		pushToMemberUnCheckMessage: string,
		dioApproverPendingMemberChangesMessage: string | null = null,
		dioApproverDisplayPlanDetail?: boolean,
	) {

		const selectedPlan = plans?.plans?.find(p => p?.code === formValues[planType]);

		if (whoWillEnrollOption === WhoWillEnrollOptions.member) {
			return <p>{formValues[`${planType}_member`] ? pushToMemberCheckMessage : pushToMemberUnCheckMessage}</p>;
		}

		if (isDioApprover && !dioApproverDisplayPlanDetail) {
			return <p>{dioApproverPendingMemberChangesMessage}</p>;
		}

		if (!formValues[planType] || formValues[planType] === DeclinedValue) {
			return <p>{declineText}</p>;
		}

		return (
			<div>
				<h5>{selectedPlan.name}</h5>
				{selectedPlan?.rates_by_tier && <p className='input-caption'>{getTierLabel(tier?.display_tier as TiersTypes, settings, labelSettings, selectedPlan?.rates_by_tier)}</p>}
				<p>{selectedPlan?.rates_by_tier && getCurrencyFormat(selectedPlan?.rates_by_tier[tier?.display_tier], labelSettings, true)}</p>
			</div>
		);
	}

	if (!hasMedical && !hasDental) {
		return null;
	}

	return (

		<DividerSection sectionText={settings?.healthPlanReviewSectionTitle} >
			{
				hasMedical &&
				<div className='divider-section-item'>
					<h4>{settings?.medicalPlanTitle}</h4>
					{
						RenderPlanReview(
							PlanTypes.M,
							settings?.declineMedicalCoverageMessage,
							coverageState?.eligible_plans?.medical,
							displayTierMedical,
							whoWillEnroll?.medical,
							isParticipant ? participantSettings?.medicalPlanToMemberReviewMessage : settings?.medicalPlanToMemberReviewMessage,
							isParticipant ? participantSettings?.medicalPlanToMemberDeclinedReviewMessage : settings?.medicalPlanToMemberDeclinedReviewMessage,
							isDioApprover ? settings?.pendingMemberForMedicalMemberChangesMessage : null,
							isDioApprover ? validatePlanContentDioApprover(coverageState?.eligible_plans?.medical, isDioApprover) : null,
						)
					}
				</div>
			}
			{
				hasDental &&
				<div className='divider-section-item'>
					<h4>{settings?.dentalPlanTitle}</h4>
					{
						RenderPlanReview(
							PlanTypes.D,
							settings?.declineDentalCoverageMessage,
							coverageState?.eligible_plans?.dental,
							displayTierDental,
							whoWillEnroll?.dental,
							isParticipant ? participantSettings?.dentalPlanToMemberReviewMessage : settings?.dentalPlanToMemberReviewMessage,
							isParticipant ? participantSettings?.dentalPlanToMemberDeclinedReviewMessage : settings?.dentalPlanToMemberDeclinedReviewMessage,
							isDioApprover ? settings?.pendingMemberForDentalMemberChangesMessage : null,
							isDioApprover ? validatePlanContentDioApprover(coverageState?.eligible_plans?.dental, isDioApprover) : null,
						)
					}
				</div>
			}

		</DividerSection>
	);
}

HealthPlanReview.defaultProps = {
	isParticipant: false,
	isDioApprover: false,
};